/**
 * المرحلة 1.35 — «مرتجع» as the desk's dialog previews it: the same rules as
 * Backend/lib/receiptRefund.js — what is left to hand back on each line, the
 * refund's worth, how much cancels what was owed and how much goes back in
 * cash. The server plans it again from the voucher it locks.
 * Tests/suites/frontend/p135-center.test.mjs keeps the two equal.
 */

export const MIN_REASON = 3;

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const text = (v, max) => (v === undefined || v === null ? null : String(v).trim().slice(0, max) || null);
const plain = (row) => (row && typeof row.toJSON === 'function' ? row.toJSON() : row || {});
const itemsOf = (r) => (Array.isArray(r.items) ? r.items : []);
const refundsOf = (r) => (Array.isArray(r.refunds) ? r.refunds : []);
const qtyOf = (item) => {
  const q = Number(item && item.quantity);
  return Number.isFinite(q) && q > 0 ? q : 1;
};

/** Everything refunded so far: its worth, and the cash handed back. */
export function refundTotals(refunds) {
  const list = Array.isArray(refunds) ? refunds : [];
  return {
    value: round2(list.reduce((s, x) => s + (Number(x.amount) || 0), 0)),
    cash: round2(list.reduce((s, x) => s + (Number(x.cash) || 0), 0)),
  };
}

/** Each line's worth on this voucher: its amount, less its share of a discount on the total. */
export function worthOf(receipt) {
  const r = plain(receipt);
  const items = itemsOf(r);
  const sum = round2(items.reduce((s, l) => s + (Number(l.amount) || 0), 0));
  const total = round2(r.total);
  if (sum <= 0 || total <= 0) return items.map(() => 0);
  if (Math.abs(sum - total) < 0.005) return items.map((l) => round2(l.amount));
  let given = 0;
  return items.map((l, i) => {
    if (i === items.length - 1) return round2(total - given);
    const share = round2((Number(l.amount) || 0) * (total / sum));
    given = round2(given + share);
    return share;
  });
}

/** The voucher's money now: what it is worth after refunds, what was kept, what is still owed. */
export function balanceOf(receipt) {
  const r = plain(receipt);
  const back = refundTotals(refundsOf(r));
  const value = round2(Number(r.total || 0) - back.value);
  const paid = round2(Number(r.paidAmount || 0) - back.cash);
  return { value, paid, due: round2(Math.max(0, value - paid)), refunded: back.value, refundedCash: back.cash };
}

/** Per line: what is left to hand back, in its count and its money. */
export function refundable(receipt) {
  const r = plain(receipt);
  const worth = worthOf(r);
  const taken = new Map();
  for (const refund of refundsOf(r)) {
    for (const l of Array.isArray(refund.lines) ? refund.lines : []) {
      const prev = taken.get(Number(l.index)) || { quantity: 0, amount: 0 };
      taken.set(Number(l.index), { quantity: round2(prev.quantity + Number(l.quantity || 0)), amount: round2(prev.amount + Number(l.amount || 0)) });
    }
  }
  return itemsOf(r).map((item, index) => {
    const quantity = qtyOf(item);
    const back = taken.get(index) || { quantity: 0, amount: 0 };
    return {
      index,
      label: item.label,
      unit: item.unit || 'once',
      quantity,
      worth: worth[index],
      leftQuantity: round2(Math.max(0, quantity - back.quantity)),
      leftAmount: round2(Math.max(0, worth[index] - back.amount)),
    };
  });
}

/**
 * The refund the desk asked for, priced, or the first problem.
 *
 * @param {object} receipt  the voucher (row or plain)
 * @param {{ lines: {index:number, quantity?:number}[], reason: string }} asked
 * @returns {{ error, code } | { refund, settles, full }}
 */
export function planRefund(receipt, { lines, reason } = {}) {
  const r = plain(receipt);
  if (r.status === 'void') return { error: 'الإيصال ملغي: مفيش مرتجع عليه', code: 'REFUND_VOID' };
  const why = text(reason, 300);
  if (!why || why.length < MIN_REASON) return { error: 'اكتب سبب المرتجع', code: 'REFUND_REASON' };
  const left = refundable(r);
  const asked = Array.isArray(lines) ? lines : [];
  if (!asked.length) return { error: 'اختار الخدمة اللي راجعة', code: 'REFUND_LINES' };
  const seen = new Set();
  const out = [];
  for (const want of asked) {
    const index = parseInt(want && want.index, 10);
    const line = left[index];
    if (!line || seen.has(index)) return { error: 'البند ده مش على الإيصال', code: 'REFUND_LINES' };
    seen.add(index);
    if (line.leftAmount <= 0 || line.leftQuantity <= 0) return { error: `«${line.label}» اترجع بالكامل قبل كده`, code: 'REFUND_DONE' };
    const q = want.quantity === undefined || want.quantity === null || want.quantity === '' ? line.leftQuantity : round2(want.quantity);
    if (!Number.isFinite(q) || q <= 0 || q > line.leftQuantity) return { error: `الكمية اللي راجعة من «${line.label}» غير صحيحة`, code: 'REFUND_QUANTITY' };
    // All of what is left takes the cents that sharing by count leaves over.
    const amount = q === line.leftQuantity ? line.leftAmount : round2((line.worth * q) / line.quantity);
    out.push({ index, label: line.label, quantity: q, unit: line.unit, amount: Math.min(amount, line.leftAmount) });
  }
  const amount = round2(out.reduce((s, l) => s + l.amount, 0));
  if (amount <= 0) return { error: 'مفيش فلوس على البنود دي ترجع', code: 'REFUND_LINES' };
  const before = balanceOf(r);
  const absorbed = round2(Math.min(amount, before.due));
  const cash = round2(amount - absorbed);
  const fullyBack = left.every((l) => {
    const now = out.find((o) => o.index === l.index);
    return round2(l.leftAmount - (now ? now.amount : 0)) <= 0;
  });
  return {
    refund: { lines: out, amount, absorbed, cash, reason: why },
    settles: round2(before.due - absorbed) <= 0,
    full: fullyBack,
  };
}

/**
 * May this account refund here? The institution's `quickReception.refunds`:
 * the desk, accounts only (confirming payments or pricing), or nobody.
 * A rendering aid; the server checks again.
 */
export function mayRefund(policy, can) {
  const who = policy && policy.refunds;
  if (who === 'off' || !can) return false;
  if (who === 'accounts') return can('PAYMENT_CONFIRM') || can('BILLING_MANAGE');
  return can('RECEIPT_ISSUE') || can('PAYMENT_CONFIRM') || can('BILLING_MANAGE');
}
